import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FormProvider } from 'react-hook-form';
import { useBizCardReview, BIZ_FIELD_KEYS, FIELD_LABELS } from '@hooks';

function getPolygonPoints(points) {
    return points.map((p) => `${p.x},${p.y}`).join(' ');
}

function renderDebugValue(value, isNA) {
    if (isNA) return 'N/A';
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
}

function confidenceStyle(conf) {
    const n = Number(conf);
    if (n > 0.9) return { background: '#dcfce7', color: '#166534' };
    if (n > 0.7) return { background: '#fef9c3', color: '#92400e' };
    return { background: '#fee2e2', color: '#991b1b' };
}

export function BizCardReview() {
    const { cardId } = useParams();
    const navigate   = useNavigate();

    const {
        methods,
        data,
        isLoading,
        isError,
        imageUrl,
        isConfirming,
        isReanalyzing,
        handleConfirm,
        handleReanalyze,
        cropOverlay,
        ocrFields,
        debugInfo,
        activeTab,
        setActiveTab,
    } = useBizCardReview(cardId);

    const { register } = methods;

    // 이미지 뷰어 상태 (컴포넌트 레벨)
    const [zoom,         setZoom]         = useState(1);
    const [offset,       setOffset]       = useState({ x: 0, y: 0 });
    const [dragging,     setDragging]     = useState(false);
    const [dragStart,    setDragStart]    = useState({ x: 0, y: 0 });
    const [imageNatural, setImageNatural] = useState({ width: 0, height: 0 });
    const viewerRef = useRef(null);

    const zoomIn    = () => setZoom((z) => Math.min(4, +(z + 0.2).toFixed(2)));
    const zoomOut   = () => setZoom((z) => Math.max(0.4, +(z - 0.2).toFixed(2)));
    const resetView = () => { setZoom(1); setOffset({ x: 0, y: 0 }); };

    const handleWheel     = (e) => { e.preventDefault(); e.stopPropagation(); if (e.deltaY < 0) zoomIn(); else zoomOut(); };
    const handleMouseDown = (e) => { setDragging(true); setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y }); };
    const handleMouseMove = (e) => { if (!dragging) return; setOffset({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y }); };
    const handleMouseUp   = () => setDragging(false);

    // passive: false 필수 — 합성이벤트 onWheel만으로는 preventDefault가 적용되지 않음
    useEffect(() => {
        const node = viewerRef.current;
        if (!node) return;
        const prevent = (e) => e.preventDefault();
        node.addEventListener('wheel', prevent, { passive: false });
        return () => node.removeEventListener('wheel', prevent);
    }, []);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
                <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full mr-2" />
                검수 데이터 로딩 중...
            </div>
        );
    }

    if (isError) {
        return (
            <div className="p-4 text-red-500 text-sm">
                데이터를 불러오지 못했습니다.
            </div>
        );
    }

    const isConfirmed = data?.review_status === 'confirmed';
    const isNA        = debugInfo?.llmEnabledForRequest === false;

    return (
        <div className="p-4">
            {/* 헤더 */}
            <div className="flex items-center gap-2 mb-4">
                <button
                    onClick={() => navigate('/biz/card-list')}
                    className="text-gray-400 hover:text-gray-600 text-sm"
                >
                    ← 목록으로
                </button>
                <h2 className="text-lg font-semibold text-gray-800">명함 검수</h2>
                {isConfirmed && (
                    <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 font-medium">
                        확정 완료
                    </span>
                )}
            </div>

            <div className="flex flex-col lg:flex-row gap-4">

                {/* ── 왼쪽: 이미지 뷰어 패널 ── */}
                <div className="bg-white rounded-lg shadow p-4 lg:w-1/2">

                    {/* 줌 컨트롤 */}
                    <div className="flex items-center gap-2 mb-3">
                        <span className="text-sm font-semibold text-gray-700">이미지</span>
                        <button type="button" onClick={zoomOut} className="text-xs px-2 py-0.5 border border-gray-300 rounded hover:bg-gray-50">−</button>
                        <button type="button" onClick={zoomIn}  className="text-xs px-2 py-0.5 border border-gray-300 rounded hover:bg-gray-50">+</button>
                        <button type="button" onClick={resetView} className="text-xs px-2 py-0.5 border border-gray-300 rounded hover:bg-gray-50">원래 크기</button>
                        <span className="text-xs text-gray-400">{zoom.toFixed(1)}×</span>
                    </div>

                    {/* 이미지 뷰어 */}
                    <div
                        ref={viewerRef}
                        onWheel={handleWheel}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                        className="rounded-lg flex items-center justify-center overflow-hidden"
                        style={{
                            height: '340px',
                            background: '#f1f5f9',
                            cursor: dragging ? 'grabbing' : 'grab',
                            overscrollBehavior: 'contain',
                            position: 'relative',
                        }}
                    >
                        {imageUrl ? (
                            <div
                                style={{
                                    position: 'relative',
                                    width: '88%',
                                    transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
                                    transformOrigin: 'center center',
                                    userSelect: 'none',
                                }}
                            >
                                <img
                                    src={imageUrl}
                                    alt="명함 원본"
                                    draggable={false}
                                    onLoad={(e) => setImageNatural({
                                        width:  e.currentTarget.naturalWidth  || 0,
                                        height: e.currentTarget.naturalHeight || 0,
                                    })}
                                    style={{
                                        display: 'block',
                                        width: '100%',
                                        borderRadius: '6px',
                                        objectFit: 'contain',
                                        userSelect: 'none',
                                    }}
                                />
                                {/* SVG crop overlay — 이미지 자연 크기 좌표 기준으로 그려짐 */}
                                {cropOverlay?.points?.length === 4 && imageNatural.width > 0 && imageNatural.height > 0 && (
                                    <svg
                                        viewBox={`0 0 ${imageNatural.width} ${imageNatural.height}`}
                                        preserveAspectRatio="none"
                                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
                                    >
                                        <polygon
                                            points={getPolygonPoints(cropOverlay.points)}
                                            fill="rgba(37,99,235,0.14)"
                                            stroke="rgba(37,99,235,0.9)"
                                            strokeWidth="10"
                                            vectorEffect="non-scaling-stroke"
                                        />
                                    </svg>
                                )}
                            </div>
                        ) : (
                            <span className="text-sm text-gray-400">이미지 없음</span>
                        )}
                    </div>

                    <p className="mt-1 text-xs text-gray-300">휠: 확대/축소 · 드래그: 이동</p>
                    {cropOverlay && (
                        <p className="text-xs text-gray-400 mt-0.5">crop 방식: {cropOverlay.method}</p>
                    )}

                    {/* LLM 디버그 패널 (접이식) */}
                    {debugInfo && (
                        <details className="mt-3 border-t border-gray-100 pt-3">
                            <summary className="text-xs font-bold text-gray-400 uppercase tracking-wide cursor-pointer select-none">
                                LLM 디버그 정보
                            </summary>
                            <div className="mt-2 space-y-1">
                                {[
                                    ['LLM 사용 옵션', renderDebugValue(debugInfo.llmEnabledForRequest, false)],
                                    ['LLM 호출 여부', renderDebugValue(debugInfo.llmCalled, isNA)],
                                    ['요청 방식',     renderDebugValue(debugInfo.llmRequestMode, isNA)],
                                    ['호출 성공',     renderDebugValue(debugInfo.llmCallSuccess, isNA)],
                                    ['Provider',      renderDebugValue(debugInfo.llmProvider, isNA)],
                                    ['Model',         renderDebugValue(debugInfo.llmModelName, isNA)],
                                    ['실패 사유',     renderDebugValue(debugInfo.llmErrorMessage, isNA)],
                                ].map(([label, value]) => (
                                    <div key={label} className="flex justify-between text-xs py-0.5 border-b border-gray-50">
                                        <span className="text-gray-400">{label}</span>
                                        <strong className="text-gray-700">{value}</strong>
                                    </div>
                                ))}
                            </div>
                        </details>
                    )}
                </div>

                {/* ── 오른쪽: 검수 패널 ── */}
                <div className="bg-white rounded-lg shadow p-4 lg:w-1/2">

                    {/* OCR / LLM 탭 */}
                    <div className="flex gap-0 mb-4 border-b border-gray-200">
                        {[
                            { key: 'llm', label: 'LLM 결과' },
                            { key: 'ocr', label: 'OCR 결과' },
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() => setActiveTab(tab.key)}
                                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                                    activeTab === tab.key
                                        ? 'border-blue-500 text-blue-700'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* ── LLM 탭: react-hook-form 편집 ── */}
                    {activeTab === 'llm' && (
                        <FormProvider {...methods}>
                            <form onSubmit={handleConfirm} className="space-y-3">
                                <div className="flex justify-between items-center mb-3">
                                    <p className="text-sm font-medium text-gray-600">
                                        LLM 추출 결과
                                        {data?.llm?.model && (
                                            <span className="ml-1 text-xs text-gray-400">({data.llm.model})</span>
                                        )}
                                    </p>
                                    {!isConfirmed && (
                                        <button
                                            type="button"
                                            onClick={handleReanalyze}
                                            disabled={isReanalyzing}
                                            className="text-xs text-blue-600 hover:underline disabled:text-gray-400 disabled:cursor-not-allowed"
                                        >
                                            {isReanalyzing ? 'LLM 실행 중...' : 'LLM 재실행'}
                                        </button>
                                    )}
                                </div>

                                {BIZ_FIELD_KEYS.map((key) => (
                                    <div key={key} className="flex items-center gap-2">
                                        <label className="text-xs text-gray-500 w-16 shrink-0">
                                            {FIELD_LABELS[key]}
                                        </label>
                                        <input
                                            {...register(key)}
                                            disabled={isConfirmed}
                                            className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm disabled:bg-gray-50 disabled:text-gray-500 focus:outline-none focus:border-blue-400"
                                        />
                                    </div>
                                ))}

                                {/* 등록 정보 */}
                                {(data?.reg_id || data?.reg_dt) && (
                                    <div className="pt-3 border-t border-gray-100">
                                        <p className="text-xs font-medium text-gray-400 mb-2">등록 정보</p>
                                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                            {data.reg_id && (
                                                <>
                                                    <span className="text-gray-400">등록자</span>
                                                    <span className="text-gray-600">{data.reg_id}</span>
                                                </>
                                            )}
                                            {data.reg_dt && (
                                                <>
                                                    <span className="text-gray-400">등록일</span>
                                                    <span className="text-gray-600">{data.reg_dt}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* 확정 버튼 */}
                                {!isConfirmed && (
                                    <div className="pt-3 border-t border-gray-100">
                                        <button
                                            type="submit"
                                            disabled={isConfirming}
                                            className="w-full bg-blue-600 text-white py-2 rounded text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                                        >
                                            {isConfirming ? '처리 중...' : '검수 확정'}
                                        </button>
                                    </div>
                                )}
                            </form>
                        </FormProvider>
                    )}

                    {/* ── OCR 탭: 읽기 전용 필드 카드 ── */}
                    {activeTab === 'ocr' && (
                        <div>
                            <p className="text-sm font-medium text-gray-600 mb-3">OCR 추출 결과 (읽기 전용)</p>
                            {ocrFields.length === 0 ? (
                                <p className="text-sm text-gray-400">OCR 결과가 없습니다.</p>
                            ) : (
                                <div className="space-y-2.5">
                                    {ocrFields.map((field) => (
                                        <div key={field.key} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                            <div className="flex justify-between items-center mb-1.5">
                                                <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                                                    {field.label}
                                                </span>
                                                {field.confidence != null && (
                                                    <span
                                                        className="text-xs font-semibold px-2 py-0.5 rounded-full"
                                                        style={confidenceStyle(field.confidence)}
                                                    >
                                                        {Math.round(Number(field.confidence) * 100)}%
                                                    </span>
                                                )}
                                            </div>
                                            {field.multiline ? (
                                                <textarea
                                                    readOnly
                                                    value={field.value}
                                                    style={{ minHeight: `${field.minHeight || 80}px`, resize: 'vertical' }}
                                                    className="w-full bg-transparent border-none outline-none text-xs text-gray-700 leading-relaxed whitespace-pre-wrap p-0"
                                                />
                                            ) : (
                                                <p className="text-sm text-gray-800">{field.value}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
